import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import CountrySelect from '../components/CountrySelect';
import { FirebaseContext, api } from 'common';
import { useTranslation } from "react-i18next";
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';

export default function LoginPage(props) {
  const { authRef, RecaptchaVerifier } = useContext(FirebaseContext);
  const { t } = useTranslation();
  const {
    facebookSignIn,
    clearLoginError,
    mobileSignIn,
    countries,
    requestEmailOtp,
    verifyEmailOtp,
    googleSignIn,
    passwordSignIn,
    passwordSignUp,
    requestPasswordReset,
    usernamePasswordLogin
  } = api;
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const [capatchaReady, setCapatchaReady] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verifier, setVerifier] = useState(null);

  const [data, setData] = React.useState({
    email: '',
    pass: '',
    country: null,
    contact: '',
    password: '',
    otp: '',
    verificationId: null,
    firstName: '',
    lastName: '',
    selectedcountry: null,
    usertype: 'customer',
    referralId: '',
    entryType: null,
    username: ''
  });

  const [otpCalled, setOtpCalled] = useState();
  const [resendTimer, setResendTimer] = useState(0);
  const [passwordMode, setPasswordMode] = useState(false);
  const [usernameMode, setUsernameMode] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });

  useEffect(() => {
    if (settings) {
      for (let i = 0; i < countries.length; i++) {
        if (countries[i].label === settings.country) {
          setData({
            country: countries[i].phone,
            selectedcountry: countries[i],
          });
        }
      }
    }
  }, [settings, countries]);

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      setCapatchaReady(true);
    }
    if (auth.profile) {
      if (auth.profile.uid) {
        const redirectPath = location && location.state && location.state.from ? location.state.from : null;
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          let role = auth.profile.usertype;
          if (role === 'admin' || role === 'fleetadmin') {
            navigate('/dashboard');
          }
          else if (role === 'driver') {
            navigate('/bookings');
          }
          else {
            navigate('/');
          }
        }
      }
    }
    if (auth.error && auth.error.flag && (auth.error.msg?.message !== t('not_logged_in'))) {
      const errorText = typeof auth.error.msg === 'string' ? auth.error.msg : (auth.error.msg?.message || auth.error.msg?.error || t('login_error'));
      if (errorText === t('require_approval')) {
        setCommonAlert({ open: true, msg: t('require_approval') })
      } else {
        if (data.contact === '' || !(!data.contact)) {
          setCommonAlert({ open: true, msg: errorText })
        }
      }
    }
    if (auth.verificationId && otpCalled) {
      setOtpCalled(false);
      setData({ ...data, verificationId: auth.verificationId });
    }
  }, [auth.profile, auth.error, auth.verificationId, navigate, data, data.email, data.contact, capatchaReady, RecaptchaVerifier, t, setData, otpCalled, setOtpCalled, location]);

  useEffect(() => {
    if (resendTimer > 0) {
      const id = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(id);
    }
  }, [resendTimer]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const vippsToken = urlParams.get('vipps_token');
    const vippsError = urlParams.get('vipps_error');
    const errorMessage = urlParams.get('message');

    if (vippsToken) {
      setIsLoading(true);
      authRef.signInWithCustomToken(vippsToken)
        .then(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch((error) => {
          console.error('Vipps authentication failed:', error);
          setCommonAlert({ open: true, msg: t('vipps_auth_error') || 'Authentication failed' });
          setIsLoading(false);
        });
    }

    if (vippsError) {
      const msg = errorMessage ? decodeURIComponent(errorMessage) : (t('vipps_login_error') || 'Vipps login failed');
      setCommonAlert({ open: true, msg });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [authRef, t]);


  const handleFacebook = (e) => {
    e.preventDefault();
    dispatch(facebookSignIn());
  }

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: '' });
    if (auth.error.flag) {
      setData({ ...data, email: '', pass: '' });
      dispatch(clearLoginError());
    }
  };

  const onInputChange = (event) => {
    setData({ ...data, [event.target.id]: event.target.value })
  }

  const handleGetOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (data.country) {
      if (data.contact) {
        if (isNaN(data.contact)) {
          setData({ ...data, entryType: 'email' });
          const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          if (re.test(data.contact)) {
            setOtpCalled(true);
            await dispatch(requestEmailOtp(data.contact));
            setResendTimer(30);
            setIsLoading(false);
          } else {
            setCommonAlert({ open: true, msg: t('proper_email') })
            setIsLoading(false);
          }
        } else {
          setData({ ...data, entryType: 'mobile' });
          let formattedNum = data.contact.replace(/ /g, '');
          if (formattedNum.length > 6) {
            if (!window.recaptchaVerifier || verifier === null) {
              window.recaptchaVerifier = new RecaptchaVerifier("sign-in-button", {
                'size': 'invisible',
                'callback': function (response) {
                  setCapatchaReady(true);
                }
              });
            }
            const appVerifier = window.recaptchaVerifier;
            setVerifier(appVerifier);
            const phoneNumber = "+" + data.country + formattedNum;
            authRef
              .signInWithPhoneNumber(phoneNumber, appVerifier)
              .then(res => {
                setData({ ...data, verificationId: res.verificationId })
                setIsLoading(false)
                appVerifier.reset();
              })
              .catch(error => {
                setCommonAlert({ open: true, msg: error.code + ": " + error.message });
                setIsLoading(false);
              });
          } else {
            setCommonAlert({ open: true, msg: t('mobile_no_blank_error') })
            setIsLoading(false);
          }
        }
      } else {
        setCommonAlert({ open: true, msg: t('contact_input_error') })
        setIsLoading(false);
      }
    } else {
      setCommonAlert({ open: true, msg: t('country_blank_error') })
      setIsLoading(false);
    }
  }

  const handleResendOTP = async (e) => {
    e.preventDefault();
    if (resendTimer === 0 && data.entryType === 'email' && data.contact) {
      setIsLoading(true);
      await dispatch(requestEmailOtp(data.contact));
      setResendTimer(30);
      setIsLoading(false);
    }
  }

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (data.otp && data.otp.length === 6) {
      if (data.entryType === 'email') {
        await dispatch(verifyEmailOtp(
          data.contact,
          data.otp
        ));
        setIsLoading(false);
      } else {
        await dispatch(mobileSignIn(
          data.verificationId,
          data.otp
        ));
        setIsLoading(false);
      }
    } else {
      setCommonAlert({ open: true, msg: t('otp_validate_error') })
      setIsLoading(false);
    }
  }

  const handleCancel = (e) => {
    setData({
      ...data,
      contact: '',
      verificationId: null,
      otp: ''
    });
    setIsLoading(false);
  }

  const onCountryChange = (object, value) => {
    if (value && value.phone) {
      setData({ ...data, country: value.phone, selectedcountry: value });
    }
  };

  const handleGoogle = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    await dispatch(googleSignIn());
    setIsLoading(false);
  }

  const handleVipps = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch('https://us-central1-reaplnprge.cloudfunctions.net/vipps_login_url');
      const jsonData = await response.json();

      if (jsonData.success && jsonData.loginUrl) {
        window.location.href = jsonData.loginUrl;
      } else {
        setCommonAlert({ open: true, msg: t('vipps_login_error') || 'Vipps login failed' });
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Vipps login error:', error);
      setCommonAlert({ open: true, msg: t('vipps_login_error') || 'Vipps login failed' });
      setIsLoading(false);
    }
  }

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!data.email || !data.password) {
      setCommonAlert({ open: true, msg: t('proper_email') });
      return;
    }
    setIsLoading(true);
    await dispatch(passwordSignIn(data.email, data.password));
    setIsLoading(false);
  }

  const handleUsernameLogin = async (e) => {
    e.preventDefault();
    if (!data.username || !data.password) {
      setCommonAlert({ open: true, msg: t('username_password_required') || 'Username and password are required' });
      return;
    }
    setIsLoading(true);
    const result = await dispatch(usernamePasswordLogin(data.username, data.password));
    if (!result.success) {
      setCommonAlert({ open: true, msg: result.error || t('login_failed') || 'Login failed' });
    }
    setIsLoading(false);
  }

  const handlePasswordSignup = async (e) => {
    e.preventDefault();
    if (!data.email || !data.password || !data.firstName || !data.lastName) {
      setCommonAlert({ open: true, msg: t('no_details_error') });
      return;
    }
    setIsLoading(true);
    await dispatch(passwordSignUp(data.email, data.password, { firstName: data.firstName, lastName: data.lastName }));
    setIsLoading(false);
  }

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!data.email) {
      setCommonAlert({ open: true, msg: t('proper_email') });
      return;
    }
    setIsLoading(true);
    const res = await dispatch(requestPasswordReset(data.email));
    setIsLoading(false);
    if (res?.success) {
      setCommonAlert({ open: true, msg: t('reset_link_sent') || 'Reset link sent to your email' });
    }
  }

  // Input field styling
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontSize: 16,
      backgroundColor: '#F9FAFB',
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: '#000', borderWidth: 2 },
    },
    '& .MuiOutlinedInput-input': { py: 1.75, px: 2 },
  };

  // Button styling
  const primaryBtnSx = {
    bgcolor: '#000',
    color: '#fff',
    borderRadius: 2,
    py: 1.75,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 16,
    boxShadow: 'none',
    '&:hover': { bgcolor: '#1F2937', boxShadow: 'none' },
    '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
  };

  const secondaryBtnSx = {
    bgcolor: '#F3F4F6',
    color: '#000',
    borderRadius: 2,
    py: 1.5,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 14,
    boxShadow: 'none',
    '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#FFFFFF',
        p: 3,
      }}
    >
      <div id="sign-in-button" />

      <Box sx={{ maxWidth: 440, width: '100%' }}>
        {/* Back button for OTP/Signup screens */}
        {(data.verificationId || showSignup) && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              if (showSignup) setShowSignup(false);
              else handleCancel();
            }}
            sx={{ mb: 2, color: '#6B7280', textTransform: 'none' }}
          >
            {t('back') || 'Back'}
          </Button>
        )}

        {/* Logo */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 28, sm: 32 },
              color: '#000',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            Plukk
          </Typography>
        </Box>

        {/* Maintenance Alert */}
        {settings?.maintenanceMode && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            {settings.maintenanceMessage || t('maintenance_active_description')}
          </Alert>
        )}

        {/* OTP Verification Screen */}
        {data.verificationId ? (
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              {t('enter_code') || 'Enter the code'}
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4 }}>
              {t('code_sent_to') || "We've sent a code to"} {data.contact}
            </Typography>

            <TextField
              fullWidth
              id="otp"
              placeholder="000000"
              value={data.otp || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 3 }}
              inputProps={{
                maxLength: 6,
                style: { letterSpacing: 8, textAlign: 'center', fontSize: 24, fontWeight: 600 }
              }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#000' }} />
              </Box>
            ) : (
              <>
                <Button fullWidth variant="contained" onClick={handleVerifyOTP} sx={{ ...primaryBtnSx, mb: 2 }}>
                  {t('verify') || 'Verify'}
                </Button>
                {data.entryType === 'email' && (
                  <Button
                    fullWidth
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0}
                    sx={secondaryBtnSx}
                  >
                    {resendTimer > 0 ? `${t('resend_in') || 'Resend in'} ${resendTimer}s` : (t('resend_otp') || 'Resend code')}
                  </Button>
                )}
              </>
            )}
          </Box>
        ) : showSignup ? (
          /* Signup Screen */
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              {t('create_account') || 'Create account'}
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4 }}>
              {t('signup_description') || 'Sign up to get started'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                id="firstName"
                placeholder={t('first_name') || 'First name'}
                value={data.firstName || ''}
                onChange={onInputChange}
                sx={inputSx}
              />
              <TextField
                fullWidth
                id="lastName"
                placeholder={t('last_name') || 'Last name'}
                value={data.lastName || ''}
                onChange={onInputChange}
                sx={inputSx}
              />
            </Box>

            <TextField
              fullWidth
              id="email"
              type="email"
              placeholder={t('email_placeholder') || 'Email address'}
              value={data.email || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            <TextField
              fullWidth
              id="password"
              type="password"
              placeholder={t('password') || 'Password'}
              value={data.password || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 3 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#000' }} />
              </Box>
            ) : (
              <Button fullWidth variant="contained" onClick={handlePasswordSignup} sx={primaryBtnSx}>
                {t('sign_up') || 'Sign up'}
              </Button>
            )}
          </Box>
        ) : passwordMode ? (
          /* Password Login Screen */
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              {t('signIn') || 'Sign in'}
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4 }}>
              {t('password_login_description') || 'Sign in with your email and password'}
            </Typography>

            <TextField
              fullWidth
              id="email"
              type="email"
              placeholder={t('email_placeholder') || 'Email address'}
              value={data.email || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            <TextField
              fullWidth
              id="password"
              type="password"
              placeholder={t('password') || 'Password'}
              value={data.password || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 3 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#000' }} />
              </Box>
            ) : (
              <>
                <Button fullWidth variant="contained" onClick={handlePasswordLogin} sx={{ ...primaryBtnSx, mb: 2 }}>
                  {t('login') || 'Log in'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Button
                    onClick={() => setShowSignup(true)}
                    sx={{ color: '#000', textTransform: 'none', fontWeight: 500, p: 0 }}
                  >
                    {t('create_account') || 'Create account'}
                  </Button>
                  <Button
                    onClick={handlePasswordReset}
                    sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 500, p: 0 }}
                  >
                    {t('forgot_password') || 'Forgot password?'}
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: 13 }}>{t('or') || 'or'}</Typography>
                </Divider>

                <Button
                  fullWidth
                  onClick={() => {
                    setPasswordMode(false);
                    setUsernameMode(true);
                  }}
                  sx={{ ...secondaryBtnSx, mb: 1 }}
                >
                  <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                  {t('use_username_login') || 'Sign in with Username'}
                </Button>

                <Button fullWidth onClick={() => { setPasswordMode(false); setUsernameMode(false); }} sx={secondaryBtnSx}>
                  {t('use_otp_login') || 'Sign in with OTP instead'}
                </Button>
              </>
            )}
          </Box>
        ) : usernameMode ? (
          /* Username/Password Login Screen */
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              {t('signIn') || 'Sign in'}
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4 }}>
              {t('username_login_description') || 'Sign in with your username and password'}
            </Typography>

            <TextField
              fullWidth
              id="username"
              type="text"
              placeholder={t('username_placeholder') || 'Username'}
              value={data.username || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            <TextField
              fullWidth
              id="password"
              type="password"
              placeholder={t('password') || 'Password'}
              value={data.password || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 3 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>
              }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#000' }} />
              </Box>
            ) : (
              <>
                <Button fullWidth variant="contained" onClick={handleUsernameLogin} sx={{ ...primaryBtnSx, mb: 3 }}>
                  {t('login') || 'Log in'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: 13 }}>{t('or') || 'or'}</Typography>
                </Divider>

                <Button
                  fullWidth
                  onClick={() => {
                    setUsernameMode(false);
                    setPasswordMode(true);
                  }}
                  sx={{ ...secondaryBtnSx, mb: 1 }}
                >
                  <EmailIcon sx={{ mr: 1, fontSize: 18 }} />
                  {t('use_email_login') || 'Sign in with Email'}
                </Button>

                <Button fullWidth onClick={() => { setPasswordMode(false); setUsernameMode(false); }} sx={secondaryBtnSx}>
                  {t('use_otp_login') || 'Sign in with OTP instead'}
                </Button>
              </>
            )}
          </Box>
        ) : (
          /* Main Login Screen - OTP Mode */
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 1 }}>
              {t('signIn') || 'Sign in'}
            </Typography>
            <Typography sx={{ color: '#6B7280', mb: 4 }}>
              {t('enter_email_or_mobile') || 'Enter your phone number or email'}
            </Typography>

            {/* Country Selection */}
            {settings && settings.AllowCountrySelection && data.selectedcountry && (
              <Box sx={{ mb: 2 }}>
                <CountrySelect
                  countries={countries}
                  label={t('select_country')}
                  value={data.selectedcountry}
                  onChange={onCountryChange}
                  disabled={false}
                />
              </Box>
            )}

            {/* Contact Input */}
            <TextField
              fullWidth
              id="contact"
              placeholder={t('contact_placeholder') || 'Phone number or email'}
              value={data.contact || ''}
              onChange={onInputChange}
              sx={{ ...inputSx, mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: '#9CA3AF' }} />
                  </InputAdornment>
                )
              }}
            />

            <Typography sx={{ color: '#9CA3AF', fontSize: 13, mb: 3 }}>
              {t('we_will_send_otp') || "We'll send you a verification code"}
            </Typography>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#000' }} />
              </Box>
            ) : (
              <>
                <Button fullWidth variant="contained" onClick={handleGetOTP} sx={{ ...primaryBtnSx, mb: 3 }}>
                  {t('continue') || 'Continue'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography sx={{ color: '#9CA3AF', fontSize: 13 }}>{t('or') || 'or'}</Typography>
                </Divider>

                {/* Social Login Options */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    fullWidth
                    startIcon={<GoogleIcon />}
                    onClick={handleGoogle}
                    sx={{
                      ...secondaryBtnSx,
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    {t('signin_with_google') || 'Continue with Google'}
                  </Button>

                  {settings && settings.VippsLoginEnabled && (
                    <Button
                      fullWidth
                      onClick={handleVipps}
                      sx={{
                        bgcolor: '#FF5B24',
                        color: '#fff',
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 14,
                        '&:hover': { bgcolor: '#E54E1B' },
                      }}
                    >
                      {t('signin_with_vipps') || 'Continue with Vipps'}
                    </Button>
                  )}

                  {settings && settings.FacebookLoginEnabled && (
                    <Button
                      fullWidth
                      onClick={handleFacebook}
                      sx={{
                        bgcolor: '#1877F2',
                        color: '#fff',
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 14,
                        '&:hover': { bgcolor: '#166FE5' },
                      }}
                    >
                      {t('signin_with_facebook') || 'Continue with Facebook'}
                    </Button>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {settings?.allow_password_login && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Button
                      fullWidth
                      onClick={() => {
                        setPasswordMode(false);
                        setUsernameMode(true);
                      }}
                      sx={{
                        color: '#667eea',
                        textTransform: 'none',
                        fontWeight: 600,
                        border: '1px solid #E5E7EB',
                        '&:hover': { bgcolor: '#F3F4F6', borderColor: '#667eea' }
                      }}
                    >
                      <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                      {t('use_username_login') || 'Sign in with Username'}
                    </Button>
                    <Typography variant="caption" sx={{ textAlign: 'center', color: '#9CA3AF' }}>
                      {t('or') || 'or'}
                    </Typography>
                  </Box>
                )}

                <Button
                  fullWidth
                  onClick={() => setPasswordMode(true)}
                  sx={{
                    color: '#6B7280',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {t('use_password_login') || 'Sign in with password instead'}
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>

      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
    </Box>
  );
}
